import { useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { FrameCorners, KnotDivider } from "@/components/ornaments";
import { useT } from "@/lib/labels";
import { useSite } from "@/context/SiteContext";

const Input = ({ label, id, ...props }) => (
  <div>
    <label className="admin-label" htmlFor={id}>{label}</label>
    <input id={id} className="admin-input" required {...props} />
  </div>
);

export default function JoinPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "signin" ? "signin" : "join");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const t = useT();
  const { settings } = useSite();
  const open = !!settings.readers_enabled;
  const legal = !!settings.legal_enabled;
  const [agree, setAgree] = useState(false);
  if (!open && tab === "join") setTab("signin");
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/contribute"} replace />;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = tab === "join" ? await register(f.name, f.email, f.password, agree) : await login(f.email, f.password, "reader");
      navigate(u.role === "admin" ? "/admin" : "/contribute");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="join-page" className="max-w-5xl mx-auto px-6 py-14 grid lg:grid-cols-2 gap-12 items-center">
      <div className="rise">
        {t("join.eyebrow") && <p className="eyebrow">{t("join.eyebrow")}</p>}
        {t("join.title") && <h1 className="font-display text-4xl sm:text-5xl mt-2">{t("join.title")}</h1>}
        <p className="mt-5 text-lg leading-relaxed text-[#3b2c1f]">Readers may draft their own tales, theories and lore of Elirios. Every scroll is read by the Lorekeeper, and only approved works are published in the <b>Readers' Chronicles</b>.</p>
        <ol className="mt-6 space-y-2 text-[17px] list-decimal pl-5">
          <li>Create your contributor account.</li>
          <li>Write and save drafts, preview them as they will appear.</li>
          <li>Submit for approval — you'll see when it is published.</li>
        </ol>
      </div>
      <form onSubmit={submit} data-testid="join-form" className="relative vellum gilt p-8 sm:p-10 rise" style={{ animationDelay: "120ms" }}>
        <FrameCorners size="w-10 h-10" />
        <div className="flex border border-[#c9a227] mb-6">
          {[...(open ? [["join", t("join.tab_join")]] : []), ["signin", t("join.tab_signin")]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`join-tab-${k}`} onClick={() => { setTab(k); setError(""); }}
              className={`flex-1 py-2.5 font-heading text-xs font-bold uppercase tracking-[0.16em] transition-colors ${tab === k ? "bg-[#991b1b] text-[#fdf6e3]" : "hover:bg-[#c9a227]/15"}`}>{l}</button>
          ))}
        </div>
        <div className="grid gap-4">
          {tab === "join" && <Input label="Your name (shown as author)" id="name" data-testid="join-name-input" value={f.name} onChange={set("name")} />}
          <Input label="Email" id="jemail" type="email" data-testid="join-email-input" value={f.email} onChange={set("email")} />
          <Input label="Password" id="jpass" type="password" minLength={tab === "join" ? 8 : undefined} data-testid="join-password-input" value={f.password} onChange={set("password")} />
        </div>
        {!open && <p data-testid="join-closed-notice" className="mt-4 text-sm italic text-[#991b1b]">Reader contributions are currently closed. New sign-ups are paused.</p>}
        {open && legal && tab === "join" && (
          <label className="flex items-start gap-2 mt-4 text-sm">
            <input type="checkbox" data-testid="join-accept-terms" checked={agree} onChange={(e) => setAgree(e.target.checked)} required className="mt-1" />
            <span>I agree to the <Link to="/legal/terms" target="_blank" className="underline">Terms of Use</Link> and <Link to="/legal/contributor" target="_blank" className="underline">Contributor Agreement</Link>, and I have read the <Link to="/legal/privacy" target="_blank" className="underline">Privacy Notice</Link>.</span>
          </label>
        )}
        {error && <p data-testid="join-error" className="mt-4 text-sm text-[#991b1b]">{error}</p>}
        <button data-testid="join-submit-button" disabled={busy} className="btn-illum theme-flame w-full justify-center mt-6 disabled:opacity-60">
          {busy ? "One moment…" : tab === "join" ? t("join.create") : t("join.tab_signin")}
        </button>
        <KnotDivider className="mt-6" />
      </form>
    </div>
  );
}
